import { CompanyDetails } from '@/types/companies';
import { ProgramSettings } from '@/types/setting';
import { Card, Typography, Box } from '@mui/material';
import { formatCurrency } from '@/utils/currency';

type CompanyFeesProps = {
  companyDetails: CompanyDetails;
  programSettings: ProgramSettings;
};

const getFeeDisplay = (fee: any, programFee: any) => {
  if (!fee || fee.value === null || fee.value === undefined) {
    return {
      text: 'Program Level Fee Applied as',
      value: formatCurrency({ amount: programFee?.value || 0 }),
      isProgramLevel: true,
    };
  }
  return {
    text: 'Company Level Fee Override Applied as',
    value: formatCurrency({ amount: fee.value }),
    isProgramLevel: false,
  };
};

export default function CompanyFees({
  companyDetails,
  programSettings,
}: CompanyFeesProps) {
  const { feeIncomingDomestic, feeOutcomeDomestic, feeMonthlySavingAccount } =
    companyDetails;

  const {
    feeIncomingDomestic: programFeeIncomingDomestic,
    feeOutcomeDomestic: programFeeOutcomeDomestic,
    feeMonthlySavingAccount: programFeeMonthlySavingAccount,
  } = programSettings;

  const incomingFee = getFeeDisplay(
    feeIncomingDomestic,
    programFeeIncomingDomestic,
  );
  const outgoingFee = getFeeDisplay(
    feeOutcomeDomestic,
    programFeeOutcomeDomestic,
  );
  const monthlyFee = getFeeDisplay(
    feeMonthlySavingAccount,
    programFeeMonthlySavingAccount,
  );

  return (
    <Card sx={{ p: 3, mb: 2 }}>
      <Typography
        variant="h6"
        sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
        Fees
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 3,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'center' },
          }}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              fontWeight: 600,
              color: 'text.primary',
              textAlign: { xs: 'left', md: 'center' },
            }}>
            Incoming Domestic
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: 'text.secondary',
              textAlign: { xs: 'left', md: 'center' },
            }}>
            {incomingFee.text}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: incomingFee.isProgramLevel
                ? 'text.secondary'
                : 'primary.main',
              fontWeight: incomingFee.isProgramLevel ? 500 : 700,
              textAlign: { xs: 'left', md: 'center' },
            }}>
            {incomingFee.value}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'center' },
          }}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              fontWeight: 600,
              color: 'text.primary',
              textAlign: { xs: 'left', md: 'center' },
            }}>
            Outgoing Domestic
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: 'text.secondary',
              textAlign: { xs: 'left', md: 'center' },
            }}>
            {outgoingFee.text}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: outgoingFee.isProgramLevel
                ? 'text.secondary'
                : 'primary.main',
              fontWeight: outgoingFee.isProgramLevel ? 500 : 700,
              textAlign: { xs: 'left', md: 'center' },
            }}>
            {outgoingFee.value}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'center' },
          }}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              fontWeight: 600,
              color: 'text.primary',
              textAlign: { xs: 'left', md: 'center' },
            }}>
            Monthly Saving Account
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: 'text.secondary',
              textAlign: { xs: 'left', md: 'center' },
            }}>
            {monthlyFee.text}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: monthlyFee.isProgramLevel
                ? 'text.secondary'
                : 'primary.main',
              fontWeight: monthlyFee.isProgramLevel ? 500 : 700,
              textAlign: { xs: 'left', md: 'center' },
            }}>
            {monthlyFee.value}
          </Typography>
        </Box>
      </Box>
    </Card>
  );
}
